import { useMemo, useState } from "react";
import { SearchableSelect, type SearchableSelectOption } from "./searchableSelect.tsx";
import { Button } from "./button.tsx";
import { useAuth } from "../../auth/authContext.tsx";
import { useAlertDialog } from "../../hooks/useAlertDialog.tsx";
import { useToast } from "../../hooks/useToast.ts";
import { saveCommonValue, type CommonCacheColumn } from "../../api/common.ts";
import styles from "./commonValueSelect.module.scss";

/** Sentinel option value for "+ Other…". Not a plausible CB value. */
const OTHER = "\u0000other";
const NONE_LABEL = "— None —";

/**
 * Dropdown over one `S_Admin_CB_Values` list (Country, Chain, Kiosk Type …): the
 * whole list opens on click and filters as you type, like WebAdmin2's
 * SetRichSelect_CB. A value that isn't in the list can only be added through
 * "+ Other…", which asks for confirmation before it is saved to the shared list —
 * a typo must never become a list entry silently.
 */
export function CommonValueSelect({
  column,
  listLabel,
  value,
  options,
  onChange,
  allowNone = true,
  className,
  ariaLabel,
}: {
  column: CommonCacheColumn;
  /** Name of the list in the confirmation, e.g. "Country". */
  listLabel: string;
  value: string;
  options: ReadonlyArray<string>;
  onChange: (value: string) => void;
  /** Offer "— None —" to clear the field. Off for required fields. */
  allowNone?: boolean;
  className?: string;
  ariaLabel?: string;
}) {
  const { appendCommonValue } = useAuth();
  const { confirm } = useAlertDialog();
  const { toast } = useToast();
  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState("");
  const [saving, setSaving] = useState(false);

  const canonByLower = useMemo(() => {
    const map = new Map<string, string>();
    for (const o of options) {
      const t = o.trim();
      if (t && !map.has(t.toLowerCase())) map.set(t.toLowerCase(), t);
    }
    return map;
  }, [options]);

  const selectOptions = useMemo<SearchableSelectOption[]>(() => {
    const out: SearchableSelectOption[] = [];
    if (allowNone) out.push({ value: "", label: NONE_LABEL });
    for (const v of canonByLower.values()) out.push({ value: v, label: v });
    out.push({ value: OTHER, label: "+ Other…" });
    return out;
  }, [canonByLower, allowNone]);

  function cancelAdd() {
    setAdding(false);
    setDraft("");
  }

  async function commitAdd() {
    const v = draft.trim();
    if (!v || saving) return;
    // Already in the list under another casing — pick that entry, add nothing.
    const existing = canonByLower.get(v.toLowerCase());
    if (existing) {
      onChange(existing);
      cancelAdd();
      return;
    }
    const ok = await confirm({
      title: `Add "${v}" to ${listLabel}?`,
      body: `"${v}" becomes a new choice in the ${listLabel} list for everyone on this endpoint. Check the spelling first.`,
      confirmLabel: "Add",
    });
    if (!ok) return;
    setSaving(true);
    try {
      appendCommonValue(await saveCommonValue(column, v));
      onChange(v);
      cancelAdd();
    } catch (err) {
      toast(err instanceof Error ? err.message : `Failed adding "${v}" to ${listLabel}`, "error");
    } finally {
      setSaving(false);
    }
  }

  if (adding) {
    return (
      <div className={className ? `${styles.addRow} ${className}` : styles.addRow}>
        <input
          className={styles.addInput}
          value={draft}
          autoFocus
          placeholder={`New ${listLabel.toLowerCase()}`}
          aria-label={`New ${listLabel} value`}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") { e.preventDefault(); void commitAdd(); }
            else if (e.key === "Escape") { e.preventDefault(); e.stopPropagation(); cancelAdd(); }
          }}
        />
        <Button variant="secondary" size="sm" onClick={cancelAdd} disabled={saving}>Cancel</Button>
        <Button variant="primary" size="sm" onClick={() => void commitAdd()} loading={saving} disabled={!draft.trim()}>Add</Button>
      </div>
    );
  }

  return (
    <SearchableSelect
      className={className}
      options={selectOptions}
      value={value}
      placeholder={allowNone ? NONE_LABEL : "Choose…"}
      ariaLabel={ariaLabel ?? listLabel}
      onChange={(v) => {
        if (v === OTHER) { setDraft(""); setAdding(true); return; }
        onChange(v);
      }}
    />
  );
}
